import { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { spinnerRing } from '../utils/bookingUtils';
import {
  fetchDeployStatus,
  subscribeDeployStatus,
  getPendingChanges,
  getDeployRunState,
  requestRedeploy,
} from '../utils/redeployUtil';
import {
  REDEPLOY_TYPICAL_MINUTES,
  REDEPLOY_RUN_STALE_MS,
} from '../constants/bookingConst';
import '../css/PendingChangesBanner.css';

/**
 * Tells an admin that their config changes are saved but not yet visible to
 * guests, and offers the one button that fixes it.
 *
 * WHY THIS EXISTS. scripts/snapshotConfig.js bakes the landing page's share of
 * /config into the build, so a first-time visitor sees the real page instead
 * of a spinner. The cost of that speed is that a saved change does not reach
 * anyone until the site is rebuilt. Before this banner, "I changed the price
 * and the website still shows the old one" was invisible from inside the app
 * and had to be explained by hand, every time.
 *
 * Renders NOTHING in the ordinary case — no pending changes, or a club that has
 * never been redeployed through the button and therefore has no baseline to
 * compare against. Same shape as OwnershipWarningBanner: a club this does not
 * apply to never sees it.
 *
 * VISIBLE TO EVERY CONFIG ADMIN, deliberately, even though only the super
 * admin can press the button. A club owner who can see "your change is not
 * live yet" can ask for it; one who sees nothing just thinks the site is
 * broken. If they press it anyway, the backend answers 403 and its wording is
 * shown as-is — the allowlist lives in one place, on the server, rather than
 * being duplicated here where it could drift.
 *
 * @param {object} props
 * @param {object} props.config - the saved club config (NOT AdminConfig's
 *   in-progress formData: this reads `lastModified`, which only exists on what
 *   was actually written).
 * @returns {JSX.Element|null}
 */
function PendingChangesBanner({ config }) {
  const [deployStatus, setDeployStatus] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // Re-renders the banner on a timer so it appears once the grace period in
  // getPendingChanges has elapsed, rather than only on the next render that
  // happens to occur.
  const [, setTick] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let active = true;

    fetchDeployStatus().then((status) => {
      if (!active) return;
      setDeployStatus(status);
      setLoaded(true);
    });

    // A live listener as well as the initial read, so the banner clears itself
    // the moment the workflow records a successful deploy. The run takes
    // minutes; an admin should not have to reload to find out it worked.
    const unsubscribe = subscribeDeployStatus((status) => {
      if (!active) return;
      setDeployStatus(status);
      setLoaded(true);
    });

    return () => { active = false; unsubscribe(); };
  }, []);

  // Cheap heartbeat so the grace period expiring actually shows. Stops once
  // the banner is up, since from then on the listener drives the state.
  useEffect(() => {
    const timer = setInterval(() => {
      if (mounted.current) setTick((n) => n + 1);
    }, 20000);
    return () => clearInterval(timer);
  }, []);

  const handleRedeploy = useCallback(async () => {
    setBusy(true);
    setResult(null);

    const response = await requestRedeploy();
    if (!mounted.current) return;

    setBusy(false);
    setResult(response);
  }, []);

  // Nothing to say until the baseline is known. Rendering a banner off a
  // half-read state would mean showing "not live yet" to a club whose site is
  // perfectly up to date.
  if (!loaded) return null;

  const { pending, hasEverDeployed } = getPendingChanges(config, deployStatus);
  const run = getDeployRunState(deployStatus);

  // Two sources for "a run is happening". The database half is what every
  // admin and every reload can see; `result` covers only the ~20 seconds
  // between the button being pressed and the workflow writing its first
  // marker, which no other tab can know about.
  const started = result?.ok === true;
  const showRunning = run.running || started;

  const runUrl = result?.runUrl || run.runUrl || null;
  const elapsedMin = run.startedAt
    ? Math.max(1, Math.round((Date.now() - run.startedAt) / 60000))
    : null;
  const runningLabel = elapsedMin
    ? `Running for about ${elapsedMin} minute${elapsedMin === 1 ? '' : 's'}…`
    : 'Starting…';
  const staleMinutes = Math.round(REDEPLOY_RUN_STALE_MS / 60000);

  // A run in progress is worth saying even on a club with nothing pending —
  // someone else may have started it, and silence reads as nothing happening.
  if (!showRunning && !pending) return null;
  if (!showRunning && !hasEverDeployed) return null;

  return (
    <div className="jt-pending-banner" role="status">
      <div className="jt-pending-banner-icon" aria-hidden="true">
        <RefreshCw size={18} />
      </div>

      <div className="jt-pending-banner-body">
        {showRunning ? (
          <>
            <p className="jt-pending-banner-title">Redeployment in progress</p>
            <p className="jt-pending-banner-text">{spinnerRing(runningLabel)}</p>
            <p className="jt-pending-banner-text">
              It usually takes {REDEPLOY_TYPICAL_MINUTES} minutes. This notice clears
              itself when the site is live — you do not need to stay on this page.
              {runUrl ? (
                <>
                  {' '}
                  <a href={runUrl} target="_blank" rel="noreferrer">Watch the run</a>.
                </>
              ) : null}
            </p>
          </>
        ) : (
          <>
            <p className="jt-pending-banner-title">Your changes are saved but not live yet</p>
            <p className="jt-pending-banner-text">
              Guests still see the previous version of the landing page. Redeploying
              rebuilds the site with your current settings and takes about{' '}
              {REDEPLOY_TYPICAL_MINUTES} minutes.
            </p>
            {run.failed ? (
              <p className="jt-pending-banner-error">
                <AlertTriangle size={14} aria-hidden="true" />
                <span>
                  The last redeploy did not finish.
                  {run.runUrl ? (
                    <>
                      {' '}
                      <a href={run.runUrl} target="_blank" rel="noreferrer">Open the run</a>
                      {' '}to see why.
                    </>
                  ) : null}
                </span>
              </p>
            ) : null}
            {run.stale ? (
              <p className="jt-pending-banner-error">
                <AlertTriangle size={14} aria-hidden="true" />
                <span>
                  A redeploy started over {staleMinutes} minutes ago and never reported
                  back. It may have been cancelled — you can start another.
                </span>
              </p>
            ) : null}
          </>
        )}

        {result && !result.ok ? (
          <p className="jt-pending-banner-error">
            <AlertTriangle size={14} aria-hidden="true" />
            <span>{result.message}</span>
          </p>
        ) : null}
      </div>

      {!showRunning ? (
        <button
          type="button"
          className="jt-pending-banner-action"
          onClick={handleRedeploy}
          disabled={busy}
        >
          {busy ? spinnerRing('Starting…') : 'Redeploy now'}
        </button>
      ) : null}
    </div>
  );
}

export default PendingChangesBanner;
